import { useState, type ReactElement } from 'react';
import { Add as AddIcon } from '@mui/icons-material';
import { Box, Button, CircularProgress, Menu, MenuItem, Typography } from '@mui/material';

import type { Asset } from '../Section/hooks/useGetAssets';
import { useUploadAsset } from '../Section/hooks/useUploadAsset';
import { AssetPickerModal } from '../../components/AssetPickerModal';
import { FileUploadModal } from '../../components/FileUploadModal';
import { ImageCropModal } from '../../components/ImageCropModal';
import { SelectedImageCard } from '../../components/SelectedImageCard';
import { InfoTooltip } from '../../../../components/InfoTooltip';
import { SEO_SOCIAL_IMAGE_TOOLTIP } from './seoTooltips';

interface SocialImagePickerProps {
  value?: string;
  organisationId: string;
  onChange: (url: string) => void;
}

const SOURCES = ['disk', 'assets'] as const;
const SOURCE_LABELS: Record<(typeof SOURCES)[number], string> = {
  disk: 'From disk',
  assets: 'From assets',
};

export function SocialImagePicker({
  value,
  organisationId,
  onChange,
}: SocialImagePickerProps): ReactElement {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [assetPickerOpen, setAssetPickerOpen] = useState(false);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [cropImageSrc, setCropImageSrc] = useState<string | null>(null);
  const [cropFileName, setCropFileName] = useState('');
  const [cropKey, setCropKey] = useState(0);
  const { mutate: uploadAsset, isPending } = useUploadAsset();

  function handleSourceClick(source: (typeof SOURCES)[number]) {
    setAnchorEl(null);
    if (source === 'disk') {
      setUploadModalOpen(true);
    } else if (source === 'assets') {
      setAssetPickerOpen(true);
    }
  }

  function handleFileSelected(file: File) {
    setUploadModalOpen(false);
    setCropFileName(file.name);
    setCropKey((key) => key + 1);
    const reader = new FileReader();
    reader.onload = () => setCropImageSrc(reader.result as string);
    reader.readAsDataURL(file);
  }

  function handleCropDone(file: File) {
    setCropImageSrc(null);
    uploadAsset({ file, organisationId }, { onSuccess: ({ url }) => onChange(url) });
  }

  function handleAssetSelect(asset: Asset) {
    // The assets list endpoint does not always return the full-size `url`;
    // fall back to the thumbnail so the selected URL is never undefined
    // (an undefined value is dropped from the SEO update payload).
    onChange(asset.url ?? asset.thumbnailUrl);
    setAssetPickerOpen(false);
  }

  return (
    <Box className="vtwo:flex vtwo:flex-col vtwo:gap-2">
      <Box className="vtwo:flex vtwo:items-center vtwo:gap-1.5">
        <Typography variant="caption" className="vtwo:text-gray-500">
          Social image
        </Typography>
        <InfoTooltip label="Social image" tooltip={SEO_SOCIAL_IMAGE_TOOLTIP} />
      </Box>

      {value && (
        <SelectedImageCard
          thumbnailUrl={value}
          isPending={isPending}
          onReplaceClick={(event) => setAnchorEl(event.currentTarget)}
          onDelete={() => onChange('')}
        />
      )}
      {!value && (
        <Button
          variant="outlined"
          startIcon={isPending ? <CircularProgress size={16} /> : <AddIcon />}
          fullWidth
          disabled={isPending}
          onClick={(event) => setAnchorEl(event.currentTarget)}
          className="vtwo:normal-case vtwo:text-gray-600 vtwo:border-plus-border vtwo:py-2 vtwo:rounded-lg"
        >
          Add social image
        </Button>
      )}

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
        {SOURCES.map((source) => (
          <MenuItem key={source} onClick={() => handleSourceClick(source)}>
            {SOURCE_LABELS[source]}
          </MenuItem>
        ))}
      </Menu>

      <FileUploadModal
        open={uploadModalOpen}
        onClose={() => setUploadModalOpen(false)}
        onFileSelected={handleFileSelected}
      />

      <ImageCropModal
        key={cropKey}
        open={Boolean(cropImageSrc)}
        imageSrc={cropImageSrc ?? ''}
        defaultFileName={cropFileName}
        onClose={() => setCropImageSrc(null)}
        onCropDone={handleCropDone}
      />

      <AssetPickerModal
        open={assetPickerOpen}
        onClose={() => setAssetPickerOpen(false)}
        onSelect={handleAssetSelect}
        organisationId={organisationId}
      />
    </Box>
  );
}
